import { Button, Flex, Group, Modal, Text, TextInput } from '@mantine/core';
import { IconDeviceFloppy } from '@tabler/icons-react';
import { useState } from 'react';
import { isValidCardBtnUrl, isValidCardImageUrl } from '../../helpers/whatsappCarouselValidation';
import AssetPickerModal from '../Assets/AssetPickerModal';

// Modal editor for a single carousel card (image + button destination URL). The button label is
// shared across all cards (Meta requires identical card structure) and lives on the parent form.
const WhatsappCarouselCardEditor = ({ card, opened, onSave, onClose, disabled }) => {
  const [cardData, setCardData] = useState(card || {});
  const [imagePickerOpen, setImagePickerOpen] = useState(false);

  // One "session" is one open of the modal. Re-seeding on the session - not just on the `card`
  // identity - also discards edits abandoned via Cancel, which reopening the same card object
  // would otherwise resurrect.
  const sessionCard = opened ? card : null;
  const [syncedCard, setSyncedCard] = useState(sessionCard);
  // Reset during render rather than from an effect: an effect runs *after* paint, so opening card
  // 2 straight after card 1 would show card 1's URLs for a frame. Setting state while rendering
  // makes React re-run this component before the browser paints. On close we only drop the
  // session marker and leave cardData alone, so the Modal can animate out with its last content.
  if (sessionCard !== syncedCard) {
    setSyncedCard(sessionCard);
    if (sessionCard) setCardData(sessionCard);
  }

  // No early return on a null card: cardData keeps the last rendered values, so the Modal can
  // animate closed showing its last content instead of unmounting abruptly. While closed,
  // Mantine renders nothing anyway.
  const validImageUrl = isValidCardImageUrl(cardData.imageUrl);
  const validBtnUrl = isValidCardBtnUrl(cardData.btnUrl);

  return (
    // The asset picker below is a nested modal: hand the focus trap over to it while it is open,
    // otherwise this modal keeps pulling focus back and the picker's inputs are unreachable.
    <Modal opened={opened} onClose={onClose} title="Carousel Card" size="lg" trapFocus={!imagePickerOpen}>
      <Text size="sm" fw={500} mb={4}>
        Image URL*
      </Text>
      <Flex gap="xs" mb="xs">
        <TextInput
          style={{ flex: 1 }}
          name="imageUrl"
          aria-label="Card Image URL"
          value={cardData.imageUrl || ''}
          placeholder="e.g. https://google.com/panda.jpg"
          error={
            cardData.imageUrl && !validImageUrl
              ? 'Invalid URL. Please check the address for typing errors or missing protocol.'
              : null
          }
          onChange={(e) => setCardData({ ...cardData, imageUrl: e.target.value })}
          disabled={disabled}
        />
        <Button onClick={() => setImagePickerOpen(true)} disabled={disabled}>
          Browse
        </Button>
      </Flex>
      <AssetPickerModal
        key="whatsapp-carousel-card-asset"
        show={imagePickerOpen}
        // Both modals default to the same z-index (Mantine's is 200), so the picker would render
        // behind this one. 200 + headroom puts it above without clearing Mantine's overlay stack.
        zIndex={300}
        close={() => setImagePickerOpen(false)}
        confirm={(asset, _, cb) => {
          setCardData((prev) => ({ ...prev, imageUrl: asset.url }));
          cb();
        }}
      />
      <Text size="xs" c="dimmed" mb="sm">
        Supported image formats: JPEG, JPG, PNG, up to 5MB. WhatsApp center-crops card images to a wide ratio (about
        1.91:1) - use landscape images around 800x418 with the subject centered, and the same ratio on every card so all
        cards render at the same height.
      </Text>

      <Text size="sm" fw={500} mb={4}>
        Button URL*
      </Text>
      <TextInput
        name="btnUrl"
        aria-label="Card Button URL"
        value={cardData.btnUrl || ''}
        placeholder="e.g. https://myshop.com/product"
        error={
          cardData.btnUrl && !validBtnUrl
            ? 'Invalid URL. Please check the address for typing errors or missing protocol.'
            : null
        }
        onChange={(e) => setCardData({ ...cardData, btnUrl: e.target.value })}
        disabled={disabled}
      />
      <Text size="xs" c="dimmed" mt="xs">
        Opened when the recipient taps this card&apos;s button. Clicks are tracked automatically.
      </Text>

      <Group justify="flex-end" mt="md">
        <Button variant="default" onClick={onClose}>
          Cancel
        </Button>
        <Button disabled={!validImageUrl || !validBtnUrl || disabled} onClick={() => onSave(cardData)}>
          <IconDeviceFloppy size={14} /> Save Card
        </Button>
      </Group>
    </Modal>
  );
};

export default WhatsappCarouselCardEditor;
